import { debounce } from "lodash";
import React, { ReactElement, useEffect, useState } from "react";
import FeedbackType from "../../FeedbackType";
import { Area } from "../../GeometricLayout";
import { editorAdapter, change } from "../../editorsSlice";
import { Attachment } from "../../injection/Attachment";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import cssMaxZIndex from "../../util/cssMaxZIndex";
import {
  sendSuggestionTakenClientFeedback,
  sendIgnoreClientFeedback,
} from "../../writeshift/WriteShift";
import usePrevious from "../underlines/hooks/usePrevious";
import { HoverLogic } from "../underlines/hooks/useUnderlineCardHover";
import {
  alertsAdapter,
  applyReplacement,
  getLowestLevelAdviceText,
} from "../underlines/writingAlertsSlice";
import getBoatLayout from "../../getBoatLayout";
import CardGate from "./CardGate";
import AddToDictionaryIcon from "../icons/AddToDictionaryIcon";
import StopHandIcon from "../icons/StopHandIcon";
import CardAction from "./CardAction";
import CardDetails from "./CardDetails";
import CardSeperator from "./CardSeperator";
import CardToggle from "./CardToggle";
import InlineCard from "./InlineCard";
import CardReplacement from "./CardReplacement";
import replaceInContentEditable from "./replaceInContentEditable";
import replaceInTextArea from "./replaceInTextArea";
import useWindowSize from "./useWindowSize";
import { PLAN_PAGE_ENDPOINT } from "../../../../../routes/endpointConstants";
import { useIgnoredPhraseRules } from "../../storage/hooks";

type Props = {
  hoverLogic: HoverLogic;
  textAreaStorage: Map<number, Attachment>;
  contentEditableAttachments: Map<number, Attachment>;
};

function useOnScroll(
  scrollLeft: number | undefined,
  scrollTop: number | undefined,
  onScroll: () => void
) {
  const previousLeft = usePrevious(scrollLeft);
  const previousTop = usePrevious(scrollTop);

  useEffect(() => {
    if (previousLeft === undefined || previousTop === undefined) {
      return;
    }
    if (previousLeft !== scrollLeft || previousTop !== scrollTop) {
      onScroll();
    }
  }, [previousLeft, previousTop, scrollLeft, scrollTop, onScroll]);
}

function useOnWindowScroll(onScroll: () => void) {
  // remove the card on the window's scroll
  useEffect(() => {
    const handler = debounce(
      () => {
        onScroll();
      },
      200,
      { leading: true }
    );
    document.addEventListener("scroll", handler);
    return () => {
      document.removeEventListener("scroll", handler);
    };
  }, [onScroll]);
}

// TODO(refactor-too-big): this file/component is too big
function InlineCardController({
  hoverLogic,
  textAreaStorage,
  contentEditableAttachments,
}: Props): ReactElement {
  const dispatch = useAppDispatch();
  const cardState = useAppSelector((state) => state.inlineCard);
  const alert = useAppSelector((state) =>
    cardState.alert
      ? alertsAdapter
          .getSelectors()
          .selectById(state.writingAlerts, cardState.alert.id)
      : undefined
  );
  const editor = useAppSelector((state) =>
    alert
      ? editorAdapter.getSelectors().selectById(state.editors, alert.editorId)
      : undefined
  );
  const scLeft = editor?.scrollLeft;
  const scTop = editor?.scrollTop;
  // Remove the card when editor scrolls
  useOnScroll(scLeft, scTop, () => {
    hoverLogic.forceNotHovering(hoverLogic.CARD_SOURCE);
  });
  // Remove the card when the window scrolls
  useOnWindowScroll(() => {
    hoverLogic.forceNotHovering(hoverLogic.CARD_SOURCE);
  });

  const windowSize = useWindowSize();

  const ignoredPhraseRuleStorage = useIgnoredPhraseRules();
  const ignoreFeature = true;
  let showDetailsFeature = true;
  const [cardArea, setCardArea] = useState<Area>();

  const index = cardState.alert?.positionIndex;
  const position =
    alert?.ui.overlayPositions?.[index !== undefined ? index : -1];
  if (!alert || !editor || !position) {
    return <></>;
  }

  const { top, left } = editor.boundingClientRect;

  // Type safety: just look at all those undefined checks >:|
  const boundaryArea =
    windowSize !== undefined &&
    windowSize.height !== undefined &&
    windowSize.width !== undefined
      ? (windowSize as Area)
      : undefined;
  const cardLayout = getBoatLayout({
    boundaryArea,
    anchor: {
      ...position,
      top: top + position.top + editor.border.top - editor.scrollTop,
      left: left + position.left + editor.border.left - editor.scrollLeft,
    },
    boat: cardArea,
  }) || {
    top:
      top +
      position.top +
      position.height +
      editor.border.top +
      editor.scrollTop,
    left: left + position.left + editor.border.left - editor.scrollLeft,
  };

  // Type Safety: toLowerCase will transform "GRAMMAR" and "SPELLING" into "grammar" and "spelling",
  // both of which are FeedbackTypes
  const type: FeedbackType =
    alert.feedback.ruleClassCategory.toLowerCase() as FeedbackType;

  let gateAction = <></>;
  let replaceEl = (
    <>
      {!alert.feedback.gated &&
        alert.feedback.replacementList.map((replacement) => {
          return (
            <CardReplacement
              key={replacement}
              type={type}
              replacement={replacement}
              onClick={() => {
                // TODO(refactor-redux): these should be in custom action function
                if (editor.docId) {
                  sendSuggestionTakenClientFeedback({
                    docId: editor.docId,
                    errorId: alert.feedback.errorId,
                    replacementSelection: replacement,
                    scoreId: alert.scoreId,
                  });
                }
                const attachment = contentEditableAttachments.get(editor.id);
                const div = attachment?.editor;
                if (div && attachment) {
                  replaceInContentEditable({
                    editor: div,
                    range: alert.ui.range,
                    replacement,
                    useExecCommand: false,
                  });
                  dispatch(applyReplacement(alert.id));
                  dispatch(
                    change({
                      id: editor.id,
                      content: div.outerHTML,
                    })
                  );
                }
                hoverLogic.forceNotHovering("card");
              }}
            />
          );
        })}
    </>
  );
  if (alert?.feedback.gated) {
    showDetailsFeature = false;
    replaceEl = <></>;
    gateAction = (
      <CardGate
        onClick={() => {
          const url = `${PLAN_PAGE_ENDPOINT}`;
          window.open(url, "_blank");
          hoverLogic.forceNotHovering(hoverLogic.CARD_SOURCE);
        }}
      />
    );
  }

  let isInformational;
  let lowestAdviceText;
  if (alert.feedback.replacementList) {
    isInformational = alert.feedback.replacementList.length === 0;
    lowestAdviceText = getLowestLevelAdviceText(alert);
  } else {
    isInformational = false;
    lowestAdviceText = "";
  }

  let ignoreAction = <></>;
  if (ignoreFeature) {
    const isSpelling = type === "spelling";
    const label = isSpelling ? "Add to dictionary" : "Ignore";
    const icon = isSpelling ? <AddToDictionaryIcon /> : <StopHandIcon />;
    ignoreAction = (
      <CardAction
        label={label}
        icon={icon}
        onClick={() => {
          const { phrase } = alert;
          if (phrase) {
            ignoredPhraseRuleStorage.toggle({
              phrase,
              rule: alert.feedback.ruleClassId,
            });
            dispatch(applyReplacement(alert.id));
          }
          hoverLogic.forceNotHovering(hoverLogic.CARD_SOURCE);
        }}
      />
    );
  }

  let cardContent;
  if (isInformational) {
    cardContent = (
      <>
        {showDetailsFeature && (
          <div className="px-3.5">
            <CardDetails>{lowestAdviceText}</CardDetails>
          </div>
        )}
        <CardSeperator />
        {ignoreAction}
      </>
    );
  } else {
    // isActionable
    cardContent = (
      <>
        {replaceEl}
        {ignoreAction}
        {showDetailsFeature && (
          <>
            <CardSeperator />
            <CardToggle title="Show details" closeTitle="Hide details">
              <div className="pl-8 pr-3.5">
                <CardDetails>{lowestAdviceText}</CardDetails>
              </div>
            </CardToggle>
          </>
        )}
        <CardSeperator />
        {gateAction}
      </>
    );
  }
  return cardState.isHovering ? (
    <div style={{ zIndex: cssMaxZIndex }}>
      <InlineCard
        ref={(cardElement) => {
          if (cardElement && !cardArea) {
            setCardArea({
              height: cardElement.offsetHeight,
              width: cardElement.offsetWidth,
            });
          }
        }}
        type={type}
        title={alert.feedback.ruleClassDescription}
        top={cardLayout.top}
        left={cardLayout.left}
        onHoverStarted={() => {
          hoverLogic.markIsHovering("card");
        }}
        onHoverEnded={() => {
          hoverLogic.markIsNotHovering("card");
        }}
      >
        {cardContent}
      </InlineCard>
    </div>
  ) : (
    <></>
  );
}

export default InlineCardController;
